import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from 'axios';
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import NewBidForm from "./NewBidForm";
import AllBids from "./AllBids";
import CurrentBids from "./CurrentBids";
import GroupBids from "./GroupBids";
import './Bids.css';

const Bids = () => {
    const [activeTab, setActiveTab] = useState('All Bids');
    const [editBidId, setEditBidId] = useState(null);
    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [drawerMounted, setDrawerMounted] = useState(false);
    const [drawerClosing, setDrawerClosing] = useState(false);

    const [selectedFilter, setSelectedFilter] = useState('all');
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState(null); // 'stock' | 'reporter' | 'staff' | null
    const filterRef = useRef(null);

    const [stocksList, setStocksList] = useState([]);      // for By Stock
    const [usersList, setUsersList] = useState([]);        // for By Staff (members only)
    const [reporterList, setReporterList] = useState([]);  // for By Reporter    

    const userType = localStorage.getItem("usertype");
    const userFullName = (localStorage.getItem("fname") || "") + " " + (localStorage.getItem("lname") || "");

    const tabs = ['All Bids', 'Current Bids', 'New Bids', 'Group Bids'];

    const handleBidCreated = () => { setActiveTab('All Bids'); };

    // Fetch stocks (for the "By Stock" submenu)
    useEffect(() => {
        const fetchStocks = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get('https://skcapi.btcjapan.co/get_stock.php', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.success) setStocksList(res.data.data);
            } catch (err) {
                console.error("Failed to load stocks:", err);
            }
        };
        fetchStocks();
    }, []);

    // Fetch users (for the "By Staff" submenu — members only)
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await axios.get('https://skcapi.btcjapan.co/fetch_users.php');
                if (res.data.Status === "200") {
                    const members = res.data.Users.filter(u => u.usertype === 'member');
                    setUsersList(members);
                }
            } catch (err) {
                console.error("Failed to load users:", err);
            }
        };
        fetchUsers();
    }, []);

    // Close the filter menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterMenuOpen(false);
                setOpenSubmenu(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);    

    // Trigger the drawer's open animation AFTER it mounts (next frame)
    useEffect(() => {
        if (showEditDrawer && !drawerClosing) {
            const id = requestAnimationFrame(() => {
                requestAnimationFrame(() => setDrawerMounted(true));
            });
            return () => cancelAnimationFrame(id);
        }
    }, [showEditDrawer, drawerClosing]);

    const openEditDrawer = (id) => {
        setEditBidId(id);
        setDrawerClosing(false);
        setDrawerMounted(false);   // start in the closed state
        setShowEditDrawer(true);
    };

    const closeEditDrawer = () => {
        if (!showEditDrawer) return;
        setDrawerClosing(true);
        setTimeout(() => {
            setShowEditDrawer(false);
            setEditBidId(null);
            setDrawerClosing(false);
            setDrawerMounted(false);
        }, 280);   // matches .drawer-closing transition duration
    };

    const applyFilter = (value) => {
        setSelectedFilter(value);
        setFilterMenuOpen(false);
        setOpenSubmenu(null);
    };

    const getFilterLabel = () => {
        if (selectedFilter === 'all') return 'All';
        if (selectedFilter === 'me') return 'Assigned to me';
        if (selectedFilter === 'everyone') return 'Assigned To Everyone';
        if (selectedFilter.startsWith('stock:')) return `Stock: ${selectedFilter.split(':')[1]}`;
        if (selectedFilter.startsWith('reporter:')) return `Reporter: ${selectedFilter.split(':')[1]}`;
        if (selectedFilter.startsWith('staff:')) return `Staff: ${selectedFilter.split(':')[1]}`;
        return 'All';
    };    

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <SuperAdminSidebar />}
                {userType === "leader" && <LeaderSidebar />}
                {userType === "manager" && <ManagerSidebar />}
                {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                <AdminNavbar pageTitle="Bids" />

                <div className="all-content">

                    {/* ========== 5 STATIC STATUS CARDS ========== */}
                    <div className="bids-stats-bar">
                        <div className="stat-box unconfirmed">
                            <div className="stat-amount">0</div>
                            <div className="stat-label">Unconfirmed</div>
                        </div>
                        <div className="stat-box confirmed">
                            <div className="stat-amount">0</div>
                            <div className="stat-label">Confirmed</div>
                        </div>
                        <div className="stat-box inprogress">
                            <div className="stat-amount">0</div>
                            <div className="stat-label">In Progress</div>
                        </div>
                        <div className="stat-box resolved">
                            <div className="stat-amount">0</div>
                            <div className="stat-label">Resolved</div>
                        </div>
                        <div className="stat-box verified">
                            <div className="stat-amount">0</div>
                            <div className="stat-label">Verified</div>
                        </div>
                    </div>
                    {/* ==================================== */}

                    <div className="bids-main-section">
                        <div className="bids-tabs">
                            <div className="bids-tabs-left">
                                {tabs.map(tab => (
                                    !(userType === 'member' && tab === 'Group Bids') && (
                                        <div
                                            key={tab}
                                            className={`bids-tab ${activeTab === tab ? 'active' : ''}`}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab}
                                        </div>
                                    )
                                ))}
                            </div>

                            {/* Filter Dropdown */}
                            <div className="leads-filter-wrap" ref={filterRef}>
                                <button
                                    type="button"
                                    className="leads-filter-trigger"
                                    onClick={() => {
                                        setFilterMenuOpen(open => !open);
                                        setOpenSubmenu(null);
                                    }}
                                >
                                    <span className="leads-filter-label">Filter by</span>
                                    <span className="leads-filter-value">{getFilterLabel()}</span>
                                    <span className={`leads-filter-caret ${filterMenuOpen ? 'open' : ''}`}>▾</span>
                                </button>

                                {filterMenuOpen && (
                                    <div className="leads-filter-menu">
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'all' ? 'active' : ''}`}
                                            onClick={() => applyFilter('all')}
                                        >
                                            All
                                        </button>
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'me' ? 'active' : ''}`}
                                            onClick={() => applyFilter('me')}
                                        >
                                            Assigned to me
                                        </button>
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'everyone' ? 'active' : ''}`}
                                            onClick={() => applyFilter('everyone')}
                                        >
                                            Assigned To Everyone
                                        </button>

                                        {/* By Stock submenu */}
                                        <div
                                            className={`leads-filter-item has-submenu ${selectedFilter.startsWith('stock:') ? 'active' : ''}`}
                                            onMouseEnter={() => setOpenSubmenu('stock')}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <span>By Stock</span>
                                            <span className="submenu-arrow">▸</span>
                                            {openSubmenu === 'stock' && (
                                                <div className="leads-submenu">
                                                    {stocksList.length === 0 && (
                                                        <div className="leads-filter-empty">No stocks available</div>
                                                    )}
                                                    {stocksList.map(stock => (
                                                        <button
                                                            key={stock.id}
                                                            type="button"
                                                            className={`leads-filter-item ${selectedFilter === `stock:${stock.chassis_number}` ? 'active' : ''}`}
                                                            onClick={() => applyFilter(`stock:${stock.chassis_number}`)}
                                                        >
                                                            {stock.chassis_number}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* By Reporter submenu */}
                                        <div
                                            className={`leads-filter-item has-submenu ${selectedFilter.startsWith('reporter:') ? 'active' : ''}`}
                                            onMouseEnter={() => setOpenSubmenu('reporter')}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <span>By Reporter</span>
                                            <span className="submenu-arrow">▸</span>
                                            {openSubmenu === 'reporter' && (
                                                <div className="leads-submenu">
                                                    {reporterList.length === 0 && (
                                                        <div className="leads-filter-empty">No reporters available</div>
                                                    )}
                                                    {reporterList.map(name => (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            className={`leads-filter-item ${selectedFilter === `reporter:${name}` ? 'active' : ''}`}
                                                            onClick={() => applyFilter(`reporter:${name}`)}
                                                        >
                                                            {name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* By Staff submenu */}
                                        <div
                                            className={`leads-filter-item has-submenu ${selectedFilter.startsWith('staff:') ? 'active' : ''}`}
                                            onMouseEnter={() => setOpenSubmenu('staff')}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <span>By Staff</span>
                                            <span className="submenu-arrow">▸</span>
                                            {openSubmenu === 'staff' && (
                                                <div className="leads-submenu">
                                                    {usersList.length === 0 && (
                                                        <div className="leads-filter-empty">No members available</div>
                                                    )}
                                                    {usersList.map(user => {
                                                        const fullName = `${user.fname} ${user.lname}`.trim();
                                                        return (
                                                            <button
                                                                key={user.id}
                                                                type="button"
                                                                className={`leads-filter-item ${selectedFilter === `staff:${user.id}` ? 'active' : ''}`}
                                                                onClick={() => applyFilter(`staff:${user.id}`)}
                                                            >
                                                                {fullName}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bids-content-container">
                            {activeTab === 'All Bids' && (
                                <AllBids
                                    openEditDrawer={openEditDrawer}
                                    filter={selectedFilter}
                                    onBidsLoaded={(bids) => {
                                        const uniqueReporters = [...new Set(bids.map(b => b.reporter).filter(Boolean))].sort();
                                        setReporterList(uniqueReporters);
                                    }}
                                />
                            )}
                            {activeTab === 'Current Bids' && <CurrentBids openEditDrawer={openEditDrawer} />}
                            {activeTab === 'New Bids' && <NewBidForm onBidCreated={handleBidCreated} currentUserFullName={userFullName} />}
                            {activeTab === 'Group Bids' && <GroupBids openEditDrawer={openEditDrawer} />}
                        </div>
                    </div>

                </div>
            </div>

            {/* ========== EDIT SIDE DRAWER (Smooth) ========== */}
            {showEditDrawer && (
                <>
                    <div
                        className={`sale-order-drawer ${
                            drawerClosing ? 'drawer-closing' : (drawerMounted ? 'drawer-open' : '')
                        }`}
                    >
                        <div className="sale-order-drawer-header">
                            <h3>Edit Bid</h3>
                            <button
                                onClick={closeEditDrawer}
                                className="sale-order-drawer-close"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <NewBidForm
                            mode="edit"
                            bidId={editBidId}
                            onBidCreated={() => {
                                closeEditDrawer();
                                setActiveTab('All Bids');
                            }}
                            currentUserFullName={userFullName}
                            onCancel={closeEditDrawer}
                        />
                    </div>

                    <div
                        className={`sale-order-drawer-overlay ${
                            drawerClosing ? 'overlay-closing' : (drawerMounted ? 'overlay-open' : '')
                        }`}
                        onClick={closeEditDrawer}
                    />
                </>
            )}
        </div>
    );
};

export default Bids;